import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Activity, 
  Clock,
  CheckCircle,
  AlertTriangle,
  X,
  Pause,
  Play,
  Download,
  Settings,
  RefreshCw,
  Archive,
  Calendar,
  HardDrive,
  Timer,
  FileArchive,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import databaseService, { BackupProgress, BackupStats } from '@/services/databaseService';

const BackupProgressMonitor = () => {
  const [selectedBackupId, setSelectedBackupId] = useState<string | null>(null);
  const [retentionDialogOpen, setRetentionDialogOpen] = useState(false);
  const [retentionPolicies, setRetentionPolicies] = useState({
    daily: 7,
    weekly: 4,
    monthly: 12,
    maxSize: 50 * 1024 * 1024 * 1024
  });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Obtener estadísticas con progreso
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['backupStatsWithProgress'],
    queryFn: databaseService.getBackupStatsWithProgress,
    refetchInterval: 5000, // Actualizar cada 5 segundos
  });

  // Obtener backups activos
  const { data: activeBackups } = useQuery({
    queryKey: ['activeBackups'],
    queryFn: databaseService.getActiveBackups,
    refetchInterval: 2000, // Actualizar cada 2 segundos
  });

  // Obtener historial de backups
  const { data: backupHistory } = useQuery({
    queryKey: ['backupHistory'],
    queryFn: databaseService.getBackupHistory,
    refetchInterval: 10000, // Actualizar cada 10 segundos
  });

  // Obtener políticas de retención
  const { data: currentRetentionPolicies } = useQuery({
    queryKey: ['retentionPolicies'],
    queryFn: databaseService.getRetentionPolicies,
  });

  // Actualizar políticas de retención cuando se cargan
  useEffect(() => {
    if (currentRetentionPolicies) {
      setRetentionPolicies(currentRetentionPolicies);
    }
  }, [currentRetentionPolicies]);

  // Cancelar backup
  const cancelBackupMutation = useMutation({
    mutationFn: databaseService.cancelBackup,
    onSuccess: () => {
      toast({
        title: "Backup cancelado",
        description: "El backup ha sido cancelado exitosamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['activeBackups'] });
      queryClient.invalidateQueries({ queryKey: ['backupHistory'] });
    },
    onError: (error: unknown) => {
      let errorMessage = "No se pudo cancelar el backup";
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Actualizar políticas de retención
  const updateRetentionMutation = useMutation({
    mutationFn: databaseService.updateRetentionPolicies,
    onSuccess: () => {
      toast({
        title: "Políticas actualizadas",
        description: "Las políticas de retención han sido actualizadas exitosamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['retentionPolicies'] });
      setRetentionDialogOpen(false);
    },
    onError: (error: unknown) => {
      let errorMessage = "No se pudieron actualizar las políticas";
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Aplicar políticas de retención
  const applyRetentionMutation = useMutation({
    mutationFn: databaseService.applyRetentionPolicies,
    onSuccess: (data) => {
      toast({
        title: "Políticas aplicadas",
        description: `Se eliminaron ${data.deletedCount} backups según las políticas de retención.`,
      });
      queryClient.invalidateQueries({ queryKey: ['backupStatsWithProgress'] });
    },
    onError: (error: unknown) => {
      let errorMessage = "No se pudieron aplicar las políticas";
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'starting':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Iniciando</Badge>;
      case 'running':
        return <Badge variant="default"><Activity className="w-3 h-3 mr-1 animate-pulse" />En progreso</Badge>;
      case 'completed':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Completado</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Error</Badge>;
      case 'cancelled':
        return <Badge variant="outline"><X className="w-3 h-3 mr-1" />Cancelado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPhaseBadge = (phase: string) => {
    switch (phase) {
      case 'initialization':
        return <Badge variant="outline">Inicializando</Badge>;
      case 'database_export':
        return <Badge variant="secondary">Exportando BD</Badge>;
      case 'compression':
        return <Badge variant="secondary">Comprimiendo</Badge>;
      case 'upload':
        return <Badge variant="secondary">Subiendo</Badge>;
      case 'cleanup':
        return <Badge variant="secondary">Limpiando</Badge>;
      case 'completed':
        return <Badge variant="success">Completado</Badge>;
      default:
        return <Badge variant="outline">{phase}</Badge>;
    }
  };

  const formatDuration = (startTime: string, endTime?: string | null) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diff = end.getTime() - start.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleRetentionSubmit = () => {
    updateRetentionMutation.mutate(retentionPolicies);
  };

  return (
    <div className="space-y-6">
      {/* Resumen del sistema */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Backups Activos</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activeBackups || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.systemStatus.isBackupRunning ? 'Ejecutándose' : 'Inactivo'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Backups</CardTitle>
            <Archive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalBackups || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.totalSizeFormatted || '0 Bytes'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uso de Almacenamiento</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.sizeUsagePercentage || 0}%</div>
            <Progress value={stats?.sizeUsagePercentage || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Último Backup</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {stats?.systemStatus.lastBackupStatus || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.systemStatus.lastBackupTime ? 
                databaseService.formatDate(stats.systemStatus.lastBackupTime) : 
                'Nunca'
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Backups activos */}
      {activeBackups && activeBackups.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Backups en Progreso
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeBackups.map((backup) => (
                <div key={backup.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <h4 className="font-medium">{backup.name}</h4>
                        <p className="text-sm text-muted-foreground">{backup.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(backup.status)}
                      {getPhaseBadge(backup.phase)}
                      {backup.status === 'running' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelBackupMutation.mutate(backup.id)}
                          disabled={cancelBackupMutation.isPending}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progreso: {backup.progress}%</span>
                      <span>Duración: {formatDuration(backup.startTime, backup.endTime)}</span>
                    </div>
                    <Progress value={backup.progress} className="h-2" />
                    {backup.currentCollection && (
                      <p className="text-xs text-muted-foreground">
                        Procesando: {backup.currentCollection}
                      </p>
                    )}
                  </div>

                  {backup.error && (
                    <div className="bg-destructive/10 text-destructive p-3 rounded text-sm">
                      <strong>Error:</strong> {backup.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historial de backups */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5" />
            Historial de Backups
          </CardTitle>
          <div className="flex gap-2">
            <Dialog open={retentionDialogOpen} onOpenChange={setRetentionDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="w-4 h-4 mr-2" />
                  Configurar Retención
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Configurar Políticas de Retención</DialogTitle>
                  <DialogDescription>
                    Establece cuántos backups mantener y el límite de almacenamiento.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="daily">Backups diarios</Label>
                      <Input
                        id="daily"
                        type="number"
                        value={retentionPolicies.daily}
                        onChange={(e) => setRetentionPolicies({
                          ...retentionPolicies,
                          daily: parseInt(e.target.value) || 0
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="weekly">Backups semanales</Label>
                      <Input
                        id="weekly"
                        type="number"
                        value={retentionPolicies.weekly}
                        onChange={(e) => setRetentionPolicies({
                          ...retentionPolicies,
                          weekly: parseInt(e.target.value) || 0
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="monthly">Backups mensuales</Label>
                      <Input
                        id="monthly"
                        type="number"
                        value={retentionPolicies.monthly}
                        onChange={(e) => setRetentionPolicies({
                          ...retentionPolicies,
                          monthly: parseInt(e.target.value) || 0
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="maxSize">Tamaño máximo (GB)</Label>
                      <Input
                        id="maxSize"
                        type="number"
                        value={Math.floor(retentionPolicies.maxSize / (1024 * 1024 * 1024))}
                        onChange={(e) => setRetentionPolicies({
                          ...retentionPolicies,
                          maxSize: (parseInt(e.target.value) || 0) * 1024 * 1024 * 1024
                        })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setRetentionDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleRetentionSubmit} disabled={updateRetentionMutation.isPending}>
                      {updateRetentionMutation.isPending && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                      Guardar
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => applyRetentionMutation.mutate()}
              disabled={applyRetentionMutation.isPending}
            >
              <Zap className="w-4 h-4 mr-2" />
              Aplicar Retención
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Backup</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Tamaño</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backupHistory?.map((backup) => (
                  <TableRow key={backup.id}>
                    <TableCell className="font-medium">{backup.name}</TableCell>
                    <TableCell>
                      <Badge variant={backup.type === 'full' ? 'default' : 'secondary'}>
                        {backup.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(backup.status)}</TableCell>
                    <TableCell>{formatDuration(backup.startTime, backup.endTime)}</TableCell>
                    <TableCell>{databaseService.formatBytes(backup.size)}</TableCell>
                    <TableCell>{databaseService.formatDate(backup.startTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupProgressMonitor;